/**
 * convAiSession.service.ts
 *
 * In-memory holder for the short-lived Conv-AI session key minted by the BFF
 * (POST /drt/api/bff/conv-ai-session). Interim auth bridge until TT-Auth.
 *
 * Rules (from the interim Conv-AI auth flow):
 *   - The key lives ONLY in memory (module variable) — never localStorage,
 *     sessionStorage or cookies. The BFF response is no-store.
 *   - The raw dispatcher_bff_sid is never read or copied into JavaScript; the
 *     BFF validates it from the httpOnly cookie when minting.
 *   - Refresh = mint again. It only succeeds while the Dispatcher session is
 *     still valid, so an expired/logged-out session naturally cuts Conv-AI off.
 *   - While the chat is open the key is refreshed PROACTIVELY, ahead of expiry,
 *     for as long as the Dispatcher session exists: every successful mint
 *     schedules the next one at `expiresAt - REFRESH_SKEW_MS`. The BFF answers
 *     401 to a mint once the Dispatcher session is gone; that stops the loop.
 *
 * Lifecycle:
 *   - `primeConvAiSession()`   when the chat opens (AlphaModal mount): mints and
 *                              starts the proactive refresh loop
 *   - `getConvAiSessionToken()` before every Conv-AI call; re-mints when the
 *                              cached key is missing or within REFRESH_SKEW_MS
 *                              of expiry; concurrent callers share one mint
 *   - `getConvAiAuthHeaders()` the Authorization bearer PLUS `X-Dispatcher-Host`:
 *                              the host the UI's normal /drt/api calls reach the
 *                              BFF with, so the shared BFF resolves the tenant
 *                              for Conv-AI's introspection exactly as it does for
 *                              every other API call. Deployed builds: the app's
 *                              own host (same-origin API calls). Dev server: the
 *                              /drt/api proxy target's host, injected by
 *                              vite.config.ts as VITE_DISPATCHER_HOST — nothing
 *                              to configure. Routing only — the BFF verifies the
 *                              session is bound to that tenant.
 *   - `invalidateConvAiSessionToken()` when Conv-AI answers 401 (key expired
 *                              or revoked early) so the next call re-mints
 *   - `clearConvAiSession()`   when the chat closes, or the app session ends
 *                              (`auth:session-expired`): forgets the key and
 *                              stops the proactive refresh loop
 */

import { logger } from '@/shared/lib/logger';
import { fetchConvAiSession } from '../api/convAiSessionApi';

/**
 * Re-mint when less than this remains. It must exceed the longest Conv-AI turn:
 * the key travels with the request and Conv-AI presents it to the BFF for its
 * own K3 calls (SessionGuard bearer path) while the turn is still running, so a
 * key sent with too little life left expires mid-turn and surfaces as a 401 the
 * UI never sees (Conv-AI reports it as a text answer). The Conv-AI calls have
 * no client timeout and the dev proxy allows 120 s, so 120 s is the floor.
 */
export const REFRESH_SKEW_MS = 120_000;
/** Fallback lifetime when the BFF response carries no usable expiresAt. */
const DEFAULT_LIFETIME_MS = 9 * 60_000;
/**
 * Shortest gap between proactive re-mints. Reached only when the BFF clamps the
 * key TTL below the skew because the Dispatcher session itself is about to end.
 */
const MIN_REFRESH_DELAY_MS = 5_000;
/** Retry delay after a transient (non-401) proactive refresh failure. */
const REFRESH_RETRY_MS = 30_000;

let _token: string | null = null;
let _expiresAtMs = 0;
let _inflight: Promise<string> | null = null;
/** True between primeConvAiSession() and clearConvAiSession(): the chat is open. */
let _refreshActive = false;
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

const isUsable = (): boolean => _token !== null && Date.now() < _expiresAtMs - REFRESH_SKEW_MS;

/** The BFF refused the mint because the Dispatcher session no longer exists. */
const isSessionGone = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && (err as { status?: unknown }).status === 401;

const cancelRefreshTimer = (): void => {
  if (_refreshTimer !== null) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
};

const scheduleRefresh = (delayMs: number): void => {
  if (!_refreshActive) {
    return;
  }
  cancelRefreshTimer();
  _refreshTimer = setTimeout(() => {
    _refreshTimer = null;
    void refreshProactively();
  }, delayMs);
};

/** Schedule the next proactive mint relative to the current key's expiry. */
const scheduleRefreshBeforeExpiry = (): void => {
  scheduleRefresh(Math.max(_expiresAtMs - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS));
};

const mint = async (): Promise<string> => {
  const session = await fetchConvAiSession();
  const parsed = Date.parse(session.expiresAt);
  _token = session.token;
  _expiresAtMs = Number.isFinite(parsed) ? parsed : Date.now() + DEFAULT_LIFETIME_MS;
  logger.debug('[ConvAiSession] key minted', { expiresAt: new Date(_expiresAtMs).toISOString() });
  scheduleRefreshBeforeExpiry();
  return session.token;
};

/** Mint, sharing one in-flight request between concurrent callers. */
const mintDeduped = (): Promise<string> => {
  if (!_inflight) {
    _inflight = mint().finally(() => {
      _inflight = null;
    });
  }
  return _inflight;
};

const stopRefresh = (): void => {
  _refreshActive = false;
  cancelRefreshTimer();
};

const handleRefreshFailure = (err: unknown, what: string): void => {
  const error = err instanceof Error ? err.message : String(err);
  if (isSessionGone(err)) {
    logger.warn(`[ConvAiSession] ${what}: dispatcher session gone, stopping refresh`, { error });
    stopRefresh();
    invalidateConvAiSessionToken();
    return;
  }
  logger.warn(`[ConvAiSession] ${what} failed, retrying`, { error, retryInMs: REFRESH_RETRY_MS });
  scheduleRefresh(REFRESH_RETRY_MS);
};

/**
 * Timer tick of the proactive loop. Mints unless a fresh key already arrived
 * (a 401 retry or a lazy mint may have run meanwhile), in which case it just
 * re-arms for that key's expiry. A 401 from the BFF means the Dispatcher
 * session is gone: stop — the next chat open (or re-login) primes again.
 * Anything else is transient: keep the loop alive and retry shortly.
 */
const refreshProactively = async (): Promise<void> => {
  if (!_refreshActive) {
    return;
  }
  if (isUsable() && _expiresAtMs - Date.now() - REFRESH_SKEW_MS > MIN_REFRESH_DELAY_MS) {
    scheduleRefreshBeforeExpiry();
    return;
  }
  try {
    await mintDeduped();
  } catch (err) {
    handleRefreshFailure(err, 'proactive refresh');
  }
};

/**
 * Returns a valid Conv-AI session key, minting/refreshing via the BFF when
 * needed. Rejects when the BFF refuses (e.g. Dispatcher session expired) — the
 * caller surfaces that as a chat error rather than sending an unauthenticated
 * request.
 */
export const getConvAiSessionToken = async (): Promise<string> => {
  if (isUsable()) {
    return _token as string;
  }
  return mintDeduped();
};

// A type alias (not an interface) so it stays assignable to fetch's HeadersInit /
// Record<string, string> without an explicit index signature.
export type ConvAiAuthHeaders = {
  Authorization: string;
  'X-Dispatcher-Host': string;
};

/**
 * The host the UI's own API calls reach the BFF with — the same Host the BFF
 * resolved the tenant from when this session was created. Deployed: the app's
 * host (API calls are same-origin). Dev server: the /drt/api proxy target's host,
 * which vite.config.ts injects as VITE_DISPATCHER_HOST (the proxy rewrites Host
 * to it via changeOrigin, so `localhost` never reaches the BFF).
 */
export const getDispatcherHost = (): string => {
  const proxied = (import.meta.env.VITE_DISPATCHER_HOST as string | undefined)?.trim();
  if (import.meta.env.DEV && proxied) {
    return proxied;
  }
  return window.location.host;
};

/** Authorization + tenant headers for Conv-AI calls. */
export const getConvAiAuthHeaders = async (): Promise<ConvAiAuthHeaders> => ({
  Authorization: `Bearer ${await getConvAiSessionToken()}`,
  'X-Dispatcher-Host': getDispatcherHost(),
});

/**
 * Warm the key when the chat feature starts so the first message does not pay
 * the mint round-trip, and start refreshing it ahead of expiry for as long as
 * the chat stays open and the Dispatcher session exists. Failures are logged,
 * not thrown — the send path retries the mint and reports the error to the
 * user if it persists; a transient failure here is retried by the loop.
 */
export const primeConvAiSession = async (): Promise<void> => {
  _refreshActive = true;
  try {
    await getConvAiSessionToken();
  } catch (err) {
    handleRefreshFailure(err, 'mint key on chat open');
  }
};

/** Drop the cached key (Conv-AI said 401) so the next call re-mints. */
export const invalidateConvAiSessionToken = (): void => {
  _token = null;
  _expiresAtMs = 0;
};

/** Forget the key entirely and stop refreshing it (chat closed / logged out). */
export const clearConvAiSession = (): void => {
  stopRefresh();
  invalidateConvAiSessionToken();
  _inflight = null;
};

// The app fires this when the BFF session is gone; a Conv-AI key minted for
// that session is dead too (the BFF cascades the revoke on logout).
if (typeof window !== 'undefined') {
  window.addEventListener('auth:session-expired', clearConvAiSession);
}
